import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { propsSenzaPermesso, usePermessi } from "@/shared/auth/permessi";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import {
  annullaPrenotazione,
  comeData,
  fetchPrenotazioni,
  giornoLungo,
  mailtoAnnullo,
  oggiIso,
  risolviEsito,
  type MailAnnullo,
  type Prenotazione,
  type StatoPrenotazione,
} from "../api";

const STATO: Record<StatoPrenotazione, { testo: string; cls: string }> = {
  in_corso: { testo: "in corso", cls: "bg-blue-soft" },
  prenotato: { testo: "prenotato", cls: "bg-green-soft" },
  rifiutato: { testo: "rifiutato", cls: "bg-red-soft" },
  esito_ignoto: { testo: "da verificare", cls: "bg-amber-soft" },
  annullato: { testo: "annullato", cls: "bg-paper-2" },
};

/** L'esito del ritiro dal tracking: quanti ordini il corriere ha scansionato.
 *  Solo per i ritiri prenotati con ordini dentro; null = niente da dire. */
export function esitoRitiro(
  p: Prenotazione,
  oggi: string,
): { testo: string; cls: string; mancanti: string[] } | null {
  if (p.stato !== "prenotato" || p.partiti === null) return null;
  const attivi = p.ordini.filter((r) => !p.lasciati.includes(r));
  if (attivi.length === 0) return null;
  const presi = attivi.filter((r) => p.partiti?.includes(r));
  const mancanti = attivi.filter((r) => !presi.includes(r));
  if (mancanti.length === 0) return { testo: "ritirato", cls: "bg-green-soft", mancanti };
  // Il giorno del ritiro le scansioni arrivano in serata: prima non è un esito.
  if (presi.length === 0 && p.data_ritiro >= oggi)
    return { testo: "in attesa del corriere", cls: "bg-paper-2", mancanti };
  if (presi.length === 0) return { testo: "nessuna scansione", cls: "bg-red-soft", mancanti };
  return {
    testo: `ritirati ${presi.length} di ${attivi.length}`,
    cls: "bg-amber-soft",
    mancanti,
  };
}

const TASTO =
  "rounded-full border border-line-2 bg-card px-3 py-1 font-semibold transition-colors hover:border-ink-4 disabled:cursor-not-allowed";

type Props = {
  filtro: { magazzino?: string; tipo?: string };
  /** Righe già caricate dalla scheda: evita una seconda chiamata. */
  iniziali?: Prenotazione[];
  /** Col genitore usato con `iniziali`: la riga aspetta la SUA promise (il
   *  reload della scheda) invece che una `iniziali` più fresca — che, se
   *  quel reload fallisce, non arriverebbe mai — e si sblocca comunque. */
  onCambiata: () => unknown;
};

export function Cronologia({ filtro, iniziali, onCambiata }: Props) {
  const [righe, setRighe] = useState<Prenotazione[]>(iniziali ?? []);
  const [errore, setErrore] = useState<string | null>(null);
  const [codici, setCodici] = useState<Record<number, string>>({});
  // Id delle righe con una richiesta in volo (annulla/esito): un Set, non un
  // solo id — due azioni su righe diverse possono essere in corso insieme, e
  // la prima che finisce non deve riaccendere l'altra.
  const [inCorso, setInCorso] = useState<Set<number>>(new Set());
  // Fallback dell'annullo: se il client di posta non si apre, l'operatore
  // copia questo blocco a mano — senza, indirizzo/oggetto/testo si perdono e
  // il ritiro resta attivo lato SDA.
  const [mailFallback, setMailFallback] = useState<{
    codice: string | null;
    mail: MailAnnullo;
  } | null>(null);
  const senzaPermesso = propsSenzaPermesso(usePermessi().puoScrivere("spedizioni"), "spedizioni");
  const chiave = JSON.stringify(filtro);
  const oggi = oggiIso();

  const ricarica = useCallback(async () => {
    try {
      const r = await fetchPrenotazioni(
        JSON.parse(chiave) as { magazzino?: string; tipo?: string },
      );
      setRighe(r);
      setErrore(null);
    } catch (e) {
      setErrore(prettifyApiError(e));
    }
  }, [chiave]);

  useEffect(() => {
    if (iniziali) {
      setRighe(iniziali);
      setErrore(null);
    } else {
      void ricarica();
    }
  }, [iniziali, ricarica]);

  function rilascia(id: number) {
    setInCorso((s) => {
      if (!s.has(id)) return s;
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }

  async function azione(id: number, fn: () => Promise<unknown>) {
    setInCorso((s) => new Set(s).add(id));
    setErrore(null);
    try {
      await fn();
      if (iniziali) {
        // Il reload lo fa il genitore (onCambiata → carica() della scheda):
        // aspettiamo la SUA promise, non una `iniziali` più fresca che, se
        // quel reload fallisce, non arriverebbe mai — la riga si sblocca
        // comunque nel `finally` sotto, con l'errore se il reload è fallito.
        await onCambiata();
      } else {
        // Standalone (i resi, sotto): nessun altro la ricarica.
        await ricarica();
        onCambiata();
      }
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      rilascia(id);
    }
  }

  async function annulla(p: Prenotazione) {
    const ok = window.confirm(
      `Segno annullato il ritiro ${p.codice ?? "senza codice"} e preparo la mail per Bracchi?`,
    );
    if (!ok) return;
    await azione(p.id, async () => {
      const esito = await annullaPrenotazione(p.id);
      setMailFallback({ codice: p.codice, mail: esito.mail });
      window.location.href = mailtoAnnullo(esito.mail);
    });
  }

  // L'errore prende il posto dell'intera tabella SOLO quando non c'è nulla
  // da mostrare: un errore di AZIONE (es. «È prenotato» senza codice) con
  // righe già in mano deve restare accanto a loro, non farle sparire — è lì
  // che sta il campo per correggere e riprovare.
  if (errore && righe.length === 0) {
    return (
      <div>
        <ErrorBanner message={errore} className="mb-2" />
        <button
          type="button"
          onClick={() => void ricarica()}
          className="rounded-full border border-line-2 px-3 py-1.5 text-[12.5px] font-bold text-ink"
        >
          Riprova
        </button>
      </div>
    );
  }
  return (
    <div>
      <ErrorBanner message={errore} className="mb-2" />
      {mailFallback && (
        <div className="mb-2 rounded-md border border-line bg-paper p-3 text-[12px] text-ink">
          <p className="mb-1.5 font-semibold">
            Se la mail non si è aperta, copiala e mandala a Bracchi (ritiro{" "}
            {mailFallback.codice ?? "senza codice"}).
          </p>
          <p>
            <b>A:</b> {mailFallback.mail.a}
          </p>
          <p>
            <b>Oggetto:</b> {mailFallback.mail.oggetto}
          </p>
          <pre className="mt-1 whitespace-pre-wrap rounded-lg border border-line bg-card p-2 tabular-nums text-[11.5px]">
            {mailFallback.mail.testo}
          </pre>
          <button
            type="button"
            onClick={() => setMailFallback(null)}
            className="mt-1.5 rounded-full border border-line-2 px-2 py-1 text-[11.5px] font-bold"
          >
            Chiudi
          </button>
        </div>
      )}
      {righe.length === 0 ? (
        <p className="text-[12.5px] text-ink">Nessun ritiro ancora.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12px] text-ink">
            <thead>
              <tr className="border-b border-line text-left text-[12px] font-extrabold">
                <th className="px-2 py-1.5">Ritiro</th>
                <th className="px-2 py-1.5">Prenotato il</th>
                <th className="px-2 py-1.5">Codice</th>
                <th className="px-2 py-1.5">Contenuto</th>
                <th className="px-2 py-1.5">Stato</th>
                <th className="px-2 py-1.5">Esito</th>
                <th className="px-2 py-1.5">Da</th>
                <th className="px-2 py-1.5">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {righe.map((p) => {
                const inCorsoQui = inCorso.has(p.id);
                const bloccata = Boolean(senzaPermesso.disabled) || inCorsoQui;
                const codiceScritto = (codici[p.id] ?? "").trim().length > 0;
                const esito = esitoRitiro(p, oggi);
                return (
                  <tr key={p.id} className="border-b border-line align-middle">
                    <td className="px-2 py-2">
                      {comeData(p.data_ritiro).toLocaleDateString("it-IT", {
                        weekday: "short",
                        day: "2-digit",
                        month: "2-digit",
                      })}
                    </td>
                    <td className="px-2 py-2">
                      {new Date(p.created_at)
                        .toLocaleString("it-IT", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: "Europe/Rome",
                        })
                        .replace(",", "")}
                    </td>
                    <td className="px-2 py-2 tabular-nums">{p.codice ?? "—"}</td>
                    <td className="px-2 py-2">
                      {p.ordini.length > 0
                        ? `${p.ordini.length} ${p.ordini.length === 1 ? "ordine" : "ordini"} · `
                        : ""}
                      {p.colli} {p.colli === 1 ? "collo" : "colli"} ·{" "}
                      {p.peso_kg.toLocaleString("it-IT")} kg
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11.5px] font-semibold leading-tight ${STATO[p.stato].cls}`}
                      >
                        {STATO[p.stato].testo}
                      </span>
                      {p.lasciati.length > 0 && (
                        <span className="ml-2 rounded-lg bg-red-soft px-[7px] py-px text-[11px] font-bold">
                          {p.lasciati.length === p.ordini.length
                            ? "mancato"
                            : `parziale: ${p.lasciati.length} ${p.lasciati.length === 1 ? "ordine lasciato" : "ordini lasciati"}`}
                        </span>
                      )}
                      {p.errore && (
                        <span className="ml-2 rounded-lg bg-red-soft px-[7px] py-px">
                          «{p.errore}»
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {esito ? (
                        <span
                          className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11.5px] font-semibold leading-tight ${esito.cls}`}
                          title={
                            esito.mancanti.length > 0
                              ? `Senza scansione: ${esito.mancanti.join(", ")}`
                              : undefined
                          }
                        >
                          {esito.testo}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-2">{p.creato_da}</td>
                    <td className="px-2 py-2 text-right">
                      {p.stato === "esito_ignoto" ? (
                        <span className="inline-flex items-center gap-1.5">
                          <input
                            aria-label={`Codice di prenotazione del ritiro di ${giornoLungo(p.data_ritiro)}`}
                            placeholder="Codice"
                            value={codici[p.id] ?? ""}
                            disabled={bloccata}
                            onChange={(e) => setCodici((c) => ({ ...c, [p.id]: e.target.value }))}
                            className="w-24 rounded-lg border border-line px-1.5 py-1 tabular-nums text-[12px] text-ink"
                          />
                          <button
                            type="button"
                            className={TASTO}
                            title={senzaPermesso.title}
                            disabled={bloccata || !codiceScritto}
                            onClick={() =>
                              void azione(p.id, () =>
                                risolviEsito(p.id, { prenotata: true, codice: codici[p.id] }),
                              )
                            }
                          >
                            {inCorsoQui ? "…" : "È prenotato"}
                          </button>
                          <button
                            type="button"
                            className={TASTO}
                            title={senzaPermesso.title}
                            disabled={bloccata}
                            onClick={() =>
                              void azione(p.id, () => risolviEsito(p.id, { prenotata: false }))
                            }
                          >
                            {inCorsoQui ? "…" : "non è prenotato"}
                          </button>
                        </span>
                      ) : p.stato === "prenotato" ? (
                        // Solo un ritiro prenotato si annulla: «in corso» ha la chiamata a
                        // SDA in volo, e il suo esito riscriverebbe l'annullo.
                        <button
                          type="button"
                          className={TASTO}
                          title={senzaPermesso.title}
                          disabled={bloccata}
                          onClick={() => void annulla(p)}
                        >
                          {inCorsoQui ? "…" : "Annulla"}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
